import {
  ChartCalculationType,
  ChartDiagramType,
  ChartInterval,
  ChartRangeDiagramType,
  type IChartAggregation,
  type IChartCommonSeriesType,
  type IChartSeries,
  ValueDisplayMethod,
} from "@/graphql";
import {
  type ChartAddEditCalculationType,
  type ChartAddEditDatapointType,
  type ChartAddEditTargetType,
  type ChartAddEditType,
  type ChartCalculationSeriesType,
  type ChartDatapointSeriesType,
  type ChartEditType,
  type ChartTargetSeriesType,
  SeriesModalViewTypes,
} from "../chartTypes";
import { removeShiftFromAggregation } from "./SeriesHelper";
import { lisaThemeLocal } from "@/modules/theme/helper/useLISAThemeProviderHelper";

const createCommonType = (
  key: string,
  aggregation: IChartAggregation,
  name: string,
  dataGranularitySecond: ChartInterval,
  index: number,
  valueDisplayMethod: ValueDisplayMethod,
  dataLabelsEnabled?: boolean,
  range = false,
  maxCalculationDateToday = false,
): IChartCommonSeriesType & { chartSeriesData: IChartSeries } => ({
  chartType: ChartDiagramType.column,
  isRange: range,
  allowRange: range,
  chartRangeType: ChartRangeDiagramType.arearange,
  yAxis: 0,
  index,
  stackName: "",
  stackIndex: 0,
  backgroundSeriesKey: "",
  color: "red",
  customColor: false,
  previousDataColor: "red",
  customPreviousDataColor: false,
  columnOverlayColor: "#80808048",
  columnOverlayLowerColor: lisaThemeLocal.value["lisa-invalid"],
  columnOverlayHigherColor: lisaThemeLocal.value["lisa-valid"],
  columnOverlayDynamicColor: true,
  columnOverlayGreater: false,
  columnOverlayTolerance: 0,
  maxCalculationDateToday,
  name,
  chartSeriesData: {
    data: [],
    aggregationSecond: {
      interval: dataGranularitySecond,
      type: ChartCalculationType.AVERAGE,
    },
    aggregation,
    key,
    valueDisplayMethod,
    visible: true,
    visibleSecond: true,
    visibleBackground: false,
    dataLabelsEnabled,
  },
});

type ReturnType<T extends SeriesModalViewTypes> = T extends SeriesModalViewTypes.calculation
  ? ChartCalculationSeriesType
  : T extends SeriesModalViewTypes.datapoint
    ? ChartDatapointSeriesType
    : ChartTargetSeriesType;

export default function defaultDataSeries<T extends SeriesModalViewTypes>(
  seriesType: T,
  series: ChartAddEditType,
  dataGranularitySecond: ChartInterval,
  chartSettings: ChartEditType,
  defaultCostCenterId?: number,
): ReturnType<T> {
  switch (seriesType) {
    case SeriesModalViewTypes.datapoint: {
      const datapointSeries: Omit<ChartDatapointSeriesType, "id"> = {
        commonType: createCommonType(
          crypto.randomUUID(),
          series.aggregation,
          series.name,
          dataGranularitySecond,
          Math.max(...chartSettings.datapoints.map((dp) => dp.commonType.index), -1) + 1 + 1000,
          series.valueDisplayMethod,
          series.dataLabelsEnabled,
          false,
          series.maxCalculationDateToday,
        ),
        dpConn: [
          { datapointTypeId: (series as ChartAddEditDatapointType).datapointTypeId as number },
        ],
        initialScheduleRow: undefined,
        ccConn: defaultCostCenterId == null ? [] : [{ costCenterId: defaultCostCenterId }],
        siteConn: [],
        options: {
          allowDataInput: true,
        },
      };
      return datapointSeries as ReturnType<T>;
    }
    case SeriesModalViewTypes.target: {
      const target = series as ChartAddEditTargetType;
      const targetSeries: Omit<ChartTargetSeriesType, "id" | "costCenterId"> & {
        costCenterId: number | undefined;
      } = {
        commonType: createCommonType(
          crypto.randomUUID(),
          series.aggregation,
          series.name,
          dataGranularitySecond,
          Math.max(...chartSettings.targets.map((tg) => tg.commonType.index), -1) + 1,
          series.valueDisplayMethod,
          series.dataLabelsEnabled,
          target.isRange,
          series.maxCalculationDateToday,
        ),
        datapointTypeId: target.datapointTypeId as number,
        costCenterId: defaultCostCenterId,
        initialScheduleRow: undefined,
      };
      targetSeries.commonType.chartType = ChartDiagramType.line;

      return targetSeries as ReturnType<T>;
    }
    case SeriesModalViewTypes.calculation: {
      const newCalculation: ChartCalculationSeriesType = {
        commonType: createCommonType(
          crypto.randomUUID(),
          series.aggregation,
          series.name,
          dataGranularitySecond,
          Math.max(...chartSettings.calculations.map((cl) => cl.commonType.index), -1) + 1 + 2000,
          series.valueDisplayMethod,
          series.dataLabelsEnabled,
          false,
          series.maxCalculationDateToday,
        ),
        calculationKeys: (series as ChartAddEditCalculationType).calculationKeys,
        unit: "",
      };

      const agg = newCalculation.commonType.chartSeriesData.aggregation;

      removeShiftFromAggregation(newCalculation.commonType);

      if (
        agg?.moving != null &&
        agg.moving.movingSteps == null &&
        agg.type === ChartCalculationType.ADDITION
      ) {
        // Cumulation (atm not possible to show on previous data)
        newCalculation.commonType.chartSeriesData.visibleSecond = false;
      }
      return newCalculation as ReturnType<T>;
    }
    default:
      throw Error("invalid series type");
  }
}
